import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/authHooks';
import { NotFound } from '@/routes/NotFound';
import { SUBSCRIBE_OFFER_STATE } from '@/features/subscribe/subscribeOfferContext';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { rememberReturnTo } from '@/session/returnTo';
import { StandaloneFrame } from '@/shell/StandaloneFrame';
import { reconcileShares } from './reconcileShares';

type State = 'loading' | 'unavailable' | 'absent' | 'rateLimited' | 'error';

/** Key the attempt by ID so changing invitation URLs also tears down stale requests. */
export function SharedLanding() {
  const { virtualAccountId = '' } = useParams();
  if (!/^\d+$/.test(virtualAccountId) || !Number.isSafeInteger(Number(virtualAccountId))) {
    // Outside the shell there is no `<main>` or skip link: this brings its own.
    return <NotFound />;
  }
  return <SharedAttempt key={virtualAccountId} virtualAccountId={Number(virtualAccountId)} />;
}

function SharedAttempt({ virtualAccountId }: { virtualAccountId: number }) {
  const { t } = useTranslation();
  const me = useMe();
  const refetch = useMeRefetch();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('loading');
  const [cooldown, setCooldown] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const mounted = useRef(false);
  const started = useRef(false);
  const primaryAction = useRef<HTMLButtonElement>(null);
  const visible = me.virtualAccounts.some((d) => d.id === virtualAccountId);
  const sharedPath = `/shared/${virtualAccountId}`;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Right after accepting, a person without a subscription sees the subscribe offer first
  // (TMMAPP-0116): the shell opens it in `offer` mode from this state, before the guest tour.
  const openVirtualAccount = useCallback(
    (offer: boolean) => {
      const target = `/v/${virtualAccountId}`;
      rememberReturnTo(target);
      void navigate(target, { replace: true, ...(offer ? { state: SUBSCRIBE_OFFER_STATE } : {}) });
    },
    [virtualAccountId, navigate],
  );

  const reconcile = useCallback(() => {
    // The shared reconcile (TMMAPP-0158); `/me` is re-read only while this page is still mounted.
    return reconcileShares(refetch, () => mounted.current).then((outcome) => {
      if (!mounted.current) return;
      if (outcome.kind === 'rateLimited') {
        setCooldown(true);
        setState('rateLimited');
      } else if (outcome.kind === 'failed' || typeof outcome.me === 'string') {
        setState('error');
      } else if (outcome.me.virtualAccounts.some((d) => d.id === virtualAccountId)) {
        openVirtualAccount(outcome.me.tier !== 'SUBSCRIBER');
      } else {
        setState(outcome.upstreamUnavailable ? 'unavailable' : 'absent');
      }
    });
  }, [virtualAccountId, refetch, openVirtualAccount]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    rememberReturnTo(sharedPath);
    if (visible) openVirtualAccount(false);
    else void reconcile();
  }, [visible, openVirtualAccount, reconcile, sharedPath]);

  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(false), 60_000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (state !== 'loading') primaryAction.current?.focus();
  }, [state]);

  const message =
    state === 'absent'
      ? me.user.email
        ? t('sharedLanding.absentEmail', { email: me.user.email })
        : t('sharedLanding.absent')
      : t(`sharedLanding.${state}`);

  return (
    <StandaloneFrame>
      <Stack spacing={3} sx={{ overflowWrap: 'anywhere' }}>
        <Typography variant="h1">{t('sharedLanding.title')}</Typography>
        <Typography role="status" aria-live="polite">
          {message}
        </Typography>
        {state === 'loading' ? (
          <CircularProgress aria-label={t('sharedLanding.loading')} />
        ) : (
          <Stack spacing={2}>
            {state === 'absent' ? (
              <Button
                ref={primaryAction}
                variant="contained"
                disabled={signingOut}
                onClick={() => {
                  setSigningOut(true);
                  void signOut({ sharedReturnTo: sharedPath });
                }}
              >
                {t('auth.signOut')}
              </Button>
            ) : (
              <Button
                ref={primaryAction}
                variant="contained"
                aria-disabled={cooldown}
                onClick={() => {
                  if (cooldown) return;
                  setState('loading');
                  void reconcile();
                }}
              >
                {t('common.retry')}
              </Button>
            )}
            {me.virtualAccounts.length > 0 && (
              <Button component={RouterLink} to="/me/virtual-accounts">
                {t('sharedLanding.virtualAccounts')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </StandaloneFrame>
  );
}
